import { useState } from "react";
import { Flag } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ReportEventDialog({ eventId, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${eventId}/report`, { reason });
      if (data.already_reported) {
        toast.info("You already reported this event — we're on it.");
      } else if (data.auto_hidden) {
        toast.success("Thanks — this event has been hidden pending review.");
      } else {
        toast.success("Thanks for the heads-up. Our team will review.");
      }
      setOpen(false);
      setReason("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't submit report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          data-testid="report-event-btn"
          disabled={disabled}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest transition-opacity hover:opacity-70 disabled:opacity-40"
          style={{ color: "var(--ink-soft)" }}
        >
          <Flag className="h-3.5 w-3.5" /> Report event
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl">Report this event</DialogTitle>
          <DialogDescription>
            Something feel off? Suspicious payment requests, fake venue, duplicate listing — tell
            us what you noticed. Multiple reports auto-hide the event pending admin review.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest">What&rsquo;s wrong?</Label>
            <Textarea
              data-testid="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              minLength={3}
              rows={5}
              placeholder="e.g. Organizer asked me to send money to a personal Venmo before applying…"
            />
          </div>
          <Button
            data-testid="report-submit"
            type="submit"
            disabled={busy || reason.trim().length < 3}
            className="btn-primary w-full py-5 rounded-full"
          >
            {busy ? "Sending…" : "Send report"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
